/**
 * One item out of a list, chosen at random and then held for the life of the
 * component.
 *
 * Three places on the site rotate a variant this way — the home hero's still,
 * the `/features` banner's still, and the footer's bear line — and all three
 * need the same two properties. The pick has to be random per visit, and it
 * has to survive a re-render: the session resolving, a download arriving or a
 * route change must never swap the image or the joke out from under someone
 * mid-read. The second half is the whole reason for the `useState` initialiser,
 * and it is a guardrail in `docs/big-bear-brand.md` rather than an incidental
 * implementation detail — so it is written once here instead of remembered
 * three times. It had already started to drift apart in the three copies.
 */

import { useState } from "react";

/**
 * The constraint is a non-empty tuple, not a plain array, so `items[0]` below
 * is a fact the compiler has checked rather than a hopeful guess: every caller
 * declares its list `as const`, and an empty one is refused at the call site.
 *
 * `T` is the whole tuple with `T[number]` as the return, rather than the more
 * obvious `readonly [T, ...T[]]` returning `T` — that form infers `T` from the
 * *first* element alone and then rejects every list whose entries are not
 * identical to it, which is all three of them. The `?? items[0]` stays
 * regardless, because `noUncheckedIndexedAccess` widens *any* computed index to
 * `| undefined`, including one that provably cannot be out of range.
 */
export function usePickedRandom<T extends readonly [unknown, ...unknown[]]>(items: T): T[number] {
  const [picked] = useState<T[number]>(
    () => items[Math.floor(Math.random() * items.length)] ?? items[0],
  );
  return picked;
}
